import { ErrorMessage } from '@components/core-ui/ErrorMessage';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { BoldButton } from '@components/core-ui/form-elements/buttons/Button/variations/BoldButton';
import { Subscription } from '@modals/ASOne/ChooseASOneSubscriptionModal/components/Subscription';
import { ModalFooter } from '@modals/core-ui/ModalFooter';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { useQuery } from '@tanstack/react-query';
import { useNotifyQueryError } from 'app/hooks/useNotifyQueryError';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchAdminBillingAccountQueryFn } from 'app/queries/billing/createFetchAdminBillingAccountQueryFn';
import { createFetchAsOnePlansQueryFn } from 'app/queries/billing/createFetchAsOnePlansQueryFn';
import React from 'react';
import { useNavigate } from 'react-router';
import { DescriptionBlock } from '../../ASOne/ChooseASOneSubscriptionModal/components/DescriptionBlock';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';

export const AdminChooseASOneSubscriptionModal = ({ onCancel, onSuccess, billingAccountId }: TProps) => {
  const { call } = useAPI();
  const { updateMe } = useAuthContext();
  const navigate = useNavigate();
  const billingAccountQuery = useQuery({
    queryKey: billingKeys.adminAccount(billingAccountId),
    queryFn: createFetchAdminBillingAccountQueryFn(call),
  });
  const plansQuery = useQuery({
    queryKey: billingKeys.allAsOnePlans(),
    queryFn: createFetchAsOnePlansQueryFn(call),
  });
  useNotifyQueryError(billingAccountQuery.error);
  useNotifyQueryError(plansQuery.error);
  const billingAccount = billingAccountQuery.data;
  const plans = plansQuery.data ?? [];
  const isLoading = plansQuery.isLoading;

  const goToContactSales: React.MouseEventHandler<HTMLButtonElement> = (evt) => {
    evt.preventDefault();
    window.scroll(0, 0);
    navigate(routes.contactSales.make());
  };

  if (!billingAccount?.id && billingAccountQuery.isError) {
    return (
      <ModalWrapper width="400px">
        <ModalTitle>Pricing Plans</ModalTitle>
        <ErrorMessage error="Failed to load the billing account. Please try again." />
        <ModalFooter>
          <OutlinedButton onClick={onCancel}>Close</OutlinedButton>
        </ModalFooter>
      </ModalWrapper>
    );
  }

  if (!billingAccount?.id) {
    return <></>;
  }

  const selectPlan = async (id: string) => {
    await updateMe();
    const plan = plans.find((item) => item.id === id);

    onSuccess({
      subscriptionProductId: id,
      planName: plan?.name ?? '',
      price: Number(plan?.price ?? 0),
      freeSeats: plan?.freeSeats || 0,
    });
  };

  const activeIds = (billingAccount?.subscriptions ?? [])
    .filter((sub) => {
      return sub.status === 'active' || sub.status === 'trialing';
    })
    .map((sub) => {
      return sub.productId;
    });
  const activePlans = plans.filter((plan) => {
    return activeIds.indexOf(plan.id) > -1;
  });
  const inactivePlans = plans.filter((plan) => {
    return activeIds.indexOf(plan.id) === -1;
  });

  return (
    <ModalWrapper width="570px" overflow="auto">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Pricing Plans</ModalTitle>
      <Content>
        {plansQuery.isError ? <ErrorMessage error="Failed to load the pricing plans. Please try again." /> : null}
        <div className="scrollContainer">
          <div className="cardContainer">
            {activePlans.map((plan) => {
              return <Subscription key={plan.id} plan={plan} isActive={true} onChoose={() => selectPlan(plan.id)} />;
            })}
            {inactivePlans.map((plan) => {
              return <Subscription key={plan.id} plan={plan} isActive={false} onChoose={() => selectPlan(plan.id)} />;
            })}
          </div>
        </div>

        <DescriptionBlock />

        <div className="buttonModalContainer">
          <BoldButton onClick={goToContactSales}>Contact Our Sales Team</BoldButton>
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
        </div>
      </Content>
    </ModalWrapper>
  );
};
